/**
 * shared/auth/tokenClaims — decodificação segura de claims de JWT.
 *
 * Lê o payload base64url de um JWT sem validar a assinatura.
 * Fonte de dados de BU para fluxos client-side (ADR-0009): o buId enviado ao
 * backend DEVE vir dos claims do token, não de seletores de UI cujos ids são mock.
 *
 * Segurança: nenhum dado do claim é persistido em storage; apenas lido em memória.
 *
 * Claim `perm` (TASK-17/TASK-23, design §2.4): o `authentication` carimba, desde a TASK-17, um
 * claim `perm` com o envelope `{"v":1,"c":<catalog_version>,"g":[["<bu_id>","<reaches
 * base64url>"], ...]}` — um par (BU, vetor de alcances) por vínculo do usuário, já RESOLVIDO
 * pelo emissor (`Azim.Authorization.PermissionClaimEnvelopeCodec`, C#). Este módulo decodifica o
 * MESMO formato em TypeScript, reaproveitando o codec do vetor de alcances de `shared/authz/`
 * (`decodeReachVector`) — nunca reimplementa a codificação por bit. `canDo`/`resolveReach`
 * (TASK-15) ainda não consomem este claim: para a versão 1 (só perfis padrão, sem perfil
 * personalizado por tenant — design §2.5/§2.7), o alcance de cada papel é determinístico a
 * partir do catálogo estático, e `perm` fica disponível aqui para quando perfis personalizados
 * por tenant (TASK-26/27/33) precisarem do alcance efetivamente emitido, em vez do alcance
 * padrão do papel.
 *
 * Fail-closed (mesmo contrato do decoder C#, REQ-06): qualquer malformação do claim `perm`
 * (JSON inválido, campo ausente, entrada de `g` que não seja um par de strings) resulta em
 * `perm` AUSENTE do retorno — nunca uma interpretação parcial, e nunca lança (o restante dos
 * claims do token continua decodificado normalmente).
 */
import { decodeReachVector } from "../authz/reachEncoding.js";
import { PERMISSION_CATALOG } from "../authz/contracts.js";
import type { Reach } from "../authz/types.js";

/** Um vínculo (business unit) do envelope do claim `perm`, com o vetor de alcances decodificado. */
export interface PermissionClaimGrant {
  /** GUID da BU que produziu este vetor de alcances. */
  readonly businessUnitId: string;
  /** Alcance de cada permissão do catálogo, indexado por `Permission.index`. */
  readonly reaches: Readonly<Record<number, Reach>>;
}

/** Envelope decodificado do claim `perm` (design §2.4). */
export interface PermissionClaimEnvelope {
  /** Versão do formato do envelope (hoje sempre 1). */
  readonly version: number;
  /** Versão do catálogo de permissões do tenant no momento da emissão. */
  readonly catalogVersion: number;
  /** Um vínculo (BU) por entrada. */
  readonly grants: readonly PermissionClaimGrant[];
}

export interface TokenClaims {
  /** GUID do tenant ao qual o usuário pertence. */
  tenant_id?: string;
  /** GUID da BU principal/padrão do usuário. */
  business_unit_id?: string;
  /**
   * Lista CSV de GUIDs das BUs no escopo do usuário.
   * Parseado para `string[]`; vazio quando ausente ou inválido.
   */
  bu_ids?: string[];
  /** Papel do usuário no tenant (ex.: "sales_ae", "tenant_admin"). */
  role?: string;
  /** Claim `perm` decodificado. Ausente quando o token não o carrega (pré-TASK-17) ou é malformado. */
  perm?: PermissionClaimEnvelope;
}

/**
 * Decodifica o ENVELOPE do claim `perm` (design §2.4): base64url de
 * `{"v":1,"c":N,"g":[[bu_id,reaches],...]}`. Fail-closed: qualquer malformação retorna
 * `undefined` (nunca lança, nunca interpreta parcialmente).
 */
function decodePermClaim(raw: unknown): PermissionClaimEnvelope | undefined {
  if (typeof raw !== "string" || raw.length === 0) return undefined;
  try {
    const standard = raw.replace(/-/g, "+").replace(/_/g, "/");
    const padded = standard + "=".repeat((4 - (standard.length % 4)) % 4);
    const parsed = JSON.parse(atob(padded)) as { v?: unknown; c?: unknown; g?: unknown };

    if (typeof parsed.v !== "number" || typeof parsed.c !== "number" || !Array.isArray(parsed.g)) {
      return undefined;
    }

    const catalogSize = PERMISSION_CATALOG.permissions.length;
    const grants: PermissionClaimGrant[] = [];
    for (const pair of parsed.g) {
      if (
        !Array.isArray(pair) ||
        pair.length !== 2 ||
        typeof pair[0] !== "string" ||
        typeof pair[1] !== "string"
      ) {
        // Fail-closed: uma entrada malformada invalida o claim inteiro (mesmo contrato do
        // PermissionClaimEnvelopeCodec.Decode em C#), nunca uma lista parcial.
        return undefined;
      }
      grants.push({ businessUnitId: pair[0], reaches: decodeReachVector(pair[1], catalogSize) });
    }

    return { version: parsed.v, catalogVersion: parsed.c, grants };
  } catch {
    return undefined;
  }
}

/**
 * Decodifica os claims do payload de um JWT (base64url), sem validar assinatura.
 *
 * Retorna objeto vazio quando o token é inválido ou o payload não é JSON válido.
 * `bu_ids` é parseado de CSV em `string[]` (ex.: "guid1,guid2" → ["guid1","guid2"]).
 */
export function decodeTokenClaims(token: string): TokenClaims {
  const parts = token.split(".");
  if (parts.length < 2) return {};
  try {
    const raw = (parts[1] ?? "").replace(/-/g, "+").replace(/_/g, "/");
    // Padding adequado para atob
    const padded = raw + "=".repeat((4 - (raw.length % 4)) % 4);
    const parsed = JSON.parse(atob(padded)) as Record<string, unknown>;

    // bu_ids: o contrato atual é CSV (string), mas aceitamos array JSON
    // defensivamente — se o IdP passar a emitir bu_ids como array, o escopo
    // de BU do usuário multi-BU não é silenciosamente descartado (ACC-LOW-001).
    const rawBuIds = parsed["bu_ids"];
    const bu_ids = (
      Array.isArray(rawBuIds)
        ? rawBuIds.map((v) => String(v))
        : typeof rawBuIds === "string"
          ? rawBuIds.split(",")
          : []
    )
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    const claims: TokenClaims = { bu_ids };
    if (typeof parsed["tenant_id"] === "string") claims.tenant_id = parsed["tenant_id"];
    if (typeof parsed["business_unit_id"] === "string")
      claims.business_unit_id = parsed["business_unit_id"];
    if (typeof parsed["role"] === "string") claims.role = parsed["role"];
    const perm = decodePermClaim(parsed["perm"]);
    if (perm) claims.perm = perm;
    return claims;
  } catch {
    return {};
  }
}
